<template>
    <q-dialog v-model="showDialog" persistent>
        <q-card class="sroi-update-dialog">
            <q-card-section class="dialog-header">
                <div class="header-content">
                    <h2 class="dialog-title">Update SROI Values</h2>
                    <p class="dialog-subtitle">Edit dollar values per star rating for each wellbeing domain</p>
                </div>
                <q-btn 
                    icon="close" 
                    flat 
                    round 
                    dense 
                    @click="closeDialog"
                    class="close-btn"
                />
            </q-card-section>


            <q-card-section class="dialog-content">
                <div v-if="loading" class="loading-state">
                    <q-spinner-dots size="40px" color="#374151" />
                    <p class="loading-text">Loading SROI values...</p>
                </div>

                <div v-else-if="error" class="error-state">
                    <q-icon name="error" size="40px" color="red" />
                    <p class="error-text">{{ error }}</p>
                    <q-btn 
                        color="secondary" 
                        label="Retry" 
                        @click="loadSroiValues"
                        no-caps
                    />
                </div>

                <div v-else class="values-container">
                    <div class="values-grid">
                        <div 
                            v-for="(value, index) in sroiValues" 
                            :key="index"
                            class="value-item"
                        >
                            <div class="value-label">
                                {{ getDisplayName(value.field_name) }}
                            </div>
                            <div class="value-input">
                                <q-input
                                    v-model="value.dollar_per_star"
                                    type="number"
                                    outlined
                                    dense
                                    prefix="$"
                                    step="0.01"
                                    min="0"
                                    :rules="[val => val >= 0 || 'Must be non-negative']"
                                    @update:model-value="(val) => value.dollar_per_star = Math.round((parseFloat(val) || 0) * 100) / 100"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </q-card-section>

            <!-- Bulk Update Section -->
            <q-card-section class="bulk-update-section">
                <q-separator class="q-mb-md" />
                <div class="bulk-update-container">
                    <h3 class="bulk-update-title">Bulk Update</h3>
                    <p class="bulk-update-subtitle">Set all values to the same amount</p>
                    <div class="bulk-update-controls">
                        <q-input
                            v-model="bulkValue"
                            type="number"
                            outlined
                            dense
                            prefix="$"
                            step="0.01"
                            min="0"
                            label="Amount for all values"
                            class="bulk-input"
                        />
                        <q-btn 
                            color="primary" 
                            label="Apply to All" 
                            dense
                            no-caps
                            outline
                            @click="applyBulkValue"
                            :disable="!bulkValue || bulkValue < 0"
                            class="bulk-btn"
                        />
                    </div>
                </div>
            </q-card-section>

            <q-card-actions class="dialog-actions">
                <q-btn 
                    flat 
                    label="Cancel" 
                    no-caps
                    @click="closeDialog"
                    class="cancel-btn"
                />
                <q-btn 
                    color="secondary" 
                    label="Update Values" 
                    no-caps
                    @click="updateSroiValues"
                    :loading="updating"
                    :disable="loading || error "
                    class="update-btn"
                />
            </q-card-actions>
        </q-card>
    </q-dialog>
</template>

<script>
import { ref, computed, watch } from 'vue'
import api from "../../services/api/api";
import notify from '@/services/util/notify';

export default {
    name: "SroiUpdateComponent",
    
    props: {
        modelValue: {
            type: Boolean,
            default: false
        }
    },

       emits: ['update:modelValue', 'values-updated'],

    setup(props, { emit }) {
        const showDialog = computed({
            get: () => props.modelValue,
            set: (value) => emit('update:modelValue', value)
        });

        const sroiValues = ref([]);
        const originalValues = ref([]);
        const loading = ref(false);
        const updating = ref(false);
        const error = ref(null);
        const bulkValue = ref(null);

        // Display name mapping for field names
        const getDisplayName = (fieldName) => {
            const nameMap = {
                'u_whanau_rating': 'Whanau Rating',
                'u_friends_rating': 'Friends Rating',
                'u_community_rating': 'Community Rating',
                'u_iwi_rating': 'Iwi Rating',
                'h_setting_goals_rating': 'Setting Goals Rating',
                'h_managing_mood_rating': 'Managing Mood Rating',
                'h_managing_habits_rating': 'Managing Habits Rating',
                'h_managing_thoughts_rating': 'Managing Thoughts Rating',
                'h_asking_help_rating': 'Asking Help Rating',
                'h_problem_solving_rating': 'Problem Solving Rating',
                'ti_moving_without_pain_rating': 'Moving Without Pain Rating',
                'ti_exercise_rating': 'Exercise Rating',
                'ti_healthy_eating_rating': 'Healthy Eating Rating',
                'ti_vising_health_pro_rating': 'Visiting Health Professional Rating',
                'ti_managing_alcohol_rating': 'Managing Alcohol Rating',
                'ti_using_maori_medicine_rating': 'Using Maori Medicine Rating',
                'ta_housing_rating': 'Housing Rating',
                'ta_transport_access_rating': 'Transport Access Rating',
                'ta_safety_rating': 'Safety Rating',
                'ta_community_centres_rating': 'Community Centres Rating',
                'ta_proximity_key_places_ratings': 'Proximity to Key Places Rating',
                'r_education_rating': 'Education Rating',
                'r_employment_rating': 'Employment Rating',
                'r_money_management_rating': 'Money Management Rating',
                'r_housing_access_rating': 'Housing Access Rating',
                'r_internet_access_rating': 'Internet Access Rating',
                'w_valuing_self_rating': 'Valuing Self Rating',
                'w_maori_connection_rating': 'Maori Connection Rating',
                'w_following_practices_rating': 'Following Practices Rating'
            };
            
            return nameMap[fieldName] || fieldName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
        };

        // Check if there are any changes
        const hasChanges = computed(() => {
            if (sroiValues.value.length !== originalValues.value.length) return true;
            
            return sroiValues.value.some((value, index) => {
                const original = originalValues.value[index];
                return !original || value.dollar_per_star !== original.dollar_per_star;
            });
        });

        // Load SROI values from API
        const loadSroiValues = async () => {
            loading.value = true;
            error.value = null;
            
            try {
                const response = await api.sroi.getSroiValues();
                
                if (response.data.success) {
                    sroiValues.value = [...response.data.data];
                    originalValues.value = [...response.data.data];
                } else {
                    throw new Error(response.data.message || 'Failed to load SROI values');
                }
            } catch (err) {
                console.error('Error loading SROI values:', err);
                error.value = err.message || 'Failed to load SROI values';
            } finally {
                loading.value = false;
            }
        };

        // Update SROI values
        const updateSroiValues = async () => {
            updating.value = true;
            
            try {
                // Ensure all values are properly formatted as decimals with max 2 decimal places
                const formattedValues = sroiValues.value.map(value => ({
                    field_name: value.field_name,
                    dollar_per_star: Math.round((parseFloat(value.dollar_per_star) || 0) * 100) / 100
                }));
                
                const response = await api.sroi.updateSroiValues(formattedValues);
                
                if (response.data.success) {
                    notify.primary('SROI values updated successfully');
                    originalValues.value = [...sroiValues.value];
                    emit('values-updated'); // Emit event to refresh parent component
                    closeDialog();
                } else {
                    throw new Error(response.data.message || 'Failed to update SROI values');
                }
            } catch (err) {
                console.error('Error updating SROI values:', err);
                notify.error(err.message || 'Failed to update SROI values');
            } finally {
                updating.value = false;
            }
        };

        // Apply bulk value to all items
        const applyBulkValue = () => {
            if (bulkValue.value !== null && bulkValue.value >= 0) {
                const roundedValue = Math.round((parseFloat(bulkValue.value) || 0) * 100) / 100;
                sroiValues.value.forEach(value => {
                    value.dollar_per_star = roundedValue;
                });
                bulkValue.value = null; // Clear the input after applying
            }
        };

        // Close dialog and reset state
        const closeDialog = () => {
            showDialog.value = false;
            bulkValue.value = null; // Clear bulk value when closing
            // Reset values to original when closing without saving
            if (hasChanges.value && !updating.value) {
                sroiValues.value = [...originalValues.value];
            }
        };

        // Watch for dialog opening to load data
        watch(showDialog, (newValue) => {
            if (newValue) {
                loadSroiValues();
            }
        });

        return {
            showDialog,
            sroiValues,
            loading,
            updating,
            error,
            hasChanges,
            bulkValue,
            getDisplayName,
            loadSroiValues,
            updateSroiValues,
            applyBulkValue,
            closeDialog
        };
    }
};
</script>

<style scoped>
.sroi-update-dialog {
    max-width: 800px;
    width: 100%;
}

.dialog-header {
    background: linear-gradient(135deg, rgba(136, 166, 143, 0.1) 0%, rgba(136, 166, 143, 0.2) 100%);
    border-bottom: 1px solid rgba(136, 166, 143, 0.3);
    padding: 1.5rem;
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
}

.header-content {
    flex: 1;
}

.dialog-title {
    color: #374151;
    font-size: 1.5rem;
    font-weight: bold;
    margin: 0 0 0.5rem 0;
}

.dialog-subtitle {
    color: #374151;
    font-size: 1rem;
    margin: 0;
    opacity: 0.8;
}

.close-btn {
    color: #374151;
    margin-left: 1rem;
}

.dialog-content {
    padding: 2rem;
    max-height: 60vh;
    overflow-y: auto;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 3rem;
    text-align: center;
}

.loading-text,
.error-text {
    color: #374151;
    font-size: 1rem;
    margin-top: 1rem;
    margin-bottom: 1rem;
}

.values-container {
    width: 100%;
}

.values-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 1rem;
}

.value-item {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.value-label {
    color: #374151;
    font-weight: 600;
    font-size: 0.9rem;
}

.value-input {
    width: 100%;
}

.dialog-actions {
    padding: 1.5rem;
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.cancel-btn {
    color: #6c757d;
}

.update-btn {
    min-width: 150px;
}

.bulk-update-section {
    background: #f8f9fa;
    border-top: 1px solid #dee2e6;
    padding: 1.5rem;
}

.bulk-update-container {
    max-width: 500px;
}

.bulk-update-title {
    color: #374151;
    font-size: 1.1rem;
    font-weight: bold;
    margin: 0 0 0.25rem 0;
}

.bulk-update-subtitle {
    color: #6c757d;
    font-size: 0.9rem;
    margin: 0 0 1rem 0;
}

.bulk-update-controls {
    display: flex;
    gap: 1rem;
    align-content: center;
}

.bulk-input {
    flex: 1;
    max-width: 250px;
}

.bulk-btn {
    min-width: 120px;
}

@media (max-width: 768px) {
    .sroi-update-dialog {
        margin: 1rem;
    }
    
    .dialog-content {
        padding: 1rem;
    }
    
    .values-grid {
        grid-template-columns: 1fr;
    }
    
    .dialog-header {
        padding: 1rem;
    }
    
    .dialog-title {
        font-size: 1.25rem;
    }
    
    .bulk-update-section {
        padding: 1rem;
    }
    
    .bulk-update-controls {
        flex-direction: column;
        align-items: stretch;
    }
    
    .bulk-input {
        max-width: none;
    }
    
    .bulk-btn {
        min-width: auto;
    }
}
</style>